import { useState, useEffect, useCallback } from 'react';

interface StatePersistenceOptions<T> {
  key: string;
  initialValue: T;
  userId?: string;
}

/**
 * Hook para persistir estado en localStorage
 * Permite que el estado sobreviva a cambios de ventana/pestaña
 */
export const useStatePersistence = <T,>({
  key,
  initialValue,
  userId
}: StatePersistenceOptions<T>) => {
  const storageKey = userId ? `${key}_${userId}` : key;

  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(storageKey);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return initialValue;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error writing to localStorage:', error);
    }
  }, [state, storageKey]);

  const clearState = useCallback(() => {
    try {
      window.localStorage.removeItem(storageKey);
      setState(initialValue);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }, [storageKey, initialValue]);

  return [state, setState, clearState] as const;
};

/**
 * Hook para persistir formularios
 * Guarda automáticamente el estado del formulario
 */
export const useFormPersistence = <T extends Record<string, any>>(
  formKey: string,
  initialValues: T,
  userId?: string
) => {
  const [values, setValues, clearValues] = useStatePersistence<T>({
    key: `form_${formKey}`,
    initialValue: initialValues,
    userId
  });

  const updateField = useCallback((field: keyof T, value: any) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  }, [setValues]);

  const updateValues = useCallback((newValues: Partial<T>) => {
    setValues(prev => ({
      ...prev,
      ...newValues
    }));
  }, [setValues]);

  const resetForm = useCallback(() => {
    clearValues();
  }, [clearValues]);

  return {
    values,
    updateField,
    updateValues,
    resetForm,
    setValues
  };
};

/**
 * Hook para persistir la ruta/tab actual
 */
export const useRoutePersistence = (defaultRoute: string = '/', userId?: string) => {
  const [currentRoute, setCurrentRoute, clearRoute] = useStatePersistence<string>({
    key: 'current_route',
    initialValue: defaultRoute,
    userId
  });

  return {
    currentRoute,
    setCurrentRoute,
    clearRoute
  };
};

/**
 * Hook para persistir estado de tabs
 */
export const useTabPersistence = (
  tabKey: string,
  defaultTab: string,
  userId?: string
) => {
  const [activeTab, setActiveTab, clearTab] = useStatePersistence<string>({
    key: `tab_${tabKey}`,
    initialValue: defaultTab,
    userId
  });

  return {
    activeTab,
    setActiveTab,
    clearTab
  };
};
